import React, { useCallback, useEffect } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import InterWebviewBridge from '@interco/inter-webview-bridge'
import MobileBridge from '@interco/inter-mobile-bridge'
import routesRefinanciamento from '@routes/routes'
import { isOldBridge } from '@services/base/oldBridge'
import { validaEnvHomeByBridge } from '@utils/functions'
import { rootBackPaths } from '@utils/rootBackPaths'
import { ApplicationState } from '@store/types'
import { BridgeService } from '@services/bridge'
import { ConveniosCodes } from '@utils/enums'
import { store } from '@store/index'
import { setBottomsheetOrigin } from '@store/refinanciamento/simulacao/actions'

interface AppGoBackType {
  App: {
    goBack: () => void
  }
}
export type WindowMobileType = AppGoBackType & typeof window

const shouldBlockHardwareBack = (isApp: boolean): boolean => isApp

export const GoBackConfig = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const iWb = InterWebviewBridge.getInstance()
  const Window = window as WindowMobileType
  const { isApp, origemGoBack } = useSelector((state: ApplicationState) => state.ui.navigation)
  const origemGoBackValue = String(origemGoBack || '')
  const shouldHandleAsRootBack = rootBackPaths.includes(location?.pathname || '')
  const isNotFirstScreen = location?.key !== 'default'
  const shouldRedirectToEmprestimoFromContratos = useCallback(() => {
    if (location?.pathname !== routesRefinanciamento.CONTRATOS) return false
    if (!isApp) return false
    if (origemGoBackValue !== 'emprestimo') return false
    return isNotFirstScreen
  }, [isApp, isNotFirstScreen, location?.pathname, origemGoBackValue])

  const redirectToGoBackOrigin = useCallback(() => {
    window.location.href = validaEnvHomeByBridge(origemGoBackValue, isApp) || ''
  }, [isApp, origemGoBackValue])

  const handleRootBackForModernBridge = useCallback(() => {
    if (
      origemGoBackValue === 'emprestimo' ||
      origemGoBackValue === 'emprestimoinss' ||
      origemGoBackValue === 'emprestimosanguelaranja' ||
      origemGoBackValue === 'consignadoHome'
    ) {
      redirectToGoBackOrigin()
      return
    }
    BridgeService.backToNative()
  }, [origemGoBackValue, redirectToGoBackOrigin])

  const handleRootBackForOldBridge = useCallback(() => {
    if (origemGoBackValue === 'emprestimo' || origemGoBackValue === 'emprestimoinss') {
      redirectToGoBackOrigin()
      return
    }
    MobileBridge.goBack()
  }, [origemGoBackValue, redirectToGoBackOrigin])

  const listenerMobileBack = useCallback(() => {
    if (shouldBlockHardwareBack(isApp)) {
      return
    }

    if (location?.pathname === routesRefinanciamento.STATUS_DOCUMENTO) {
      const state = store.getState() as ApplicationState
      const { convenioCodigo } = state.refinanciamento.contratos
      if (convenioCodigo === ConveniosCodes.INSS || convenioCodigo === ConveniosCodes.CTPS) {
        store.dispatch(setBottomsheetOrigin(true))
      }
      navigate(routesRefinanciamento.RESULTADO_SIMULACAO)
      return
    }

    if (location?.pathname === routesRefinanciamento.RESULTADO_SIMULACAO) {
      navigate(routesRefinanciamento.CONTRATOS)
      return
    }

    if (shouldRedirectToEmprestimoFromContratos()) {
      redirectToGoBackOrigin()
      return
    }

    if (shouldHandleAsRootBack) {
      handleRootBackForModernBridge()
      return
    }

    navigate(-1)
  }, [
    handleRootBackForModernBridge,
    isApp,
    location?.pathname,
    navigate,
    redirectToGoBackOrigin,
    shouldHandleAsRootBack,
    shouldRedirectToEmprestimoFromContratos,
  ])

  useEffect(() => {
    if (isOldBridge()) {
      Window.App = {
        goBack: () => {
          try {
            if (shouldBlockHardwareBack(isApp)) {
              return
            }

            if (shouldRedirectToEmprestimoFromContratos()) {
              redirectToGoBackOrigin()
              return
            }

            if (shouldHandleAsRootBack) {
              handleRootBackForOldBridge()
              return
            }

            navigate(-1)
          } catch {
            // Error handling without logging
          }
        },
      }
    } else {
      iWb.addWebviewEventListener('GO_BACK', listenerMobileBack)
      return () => {
        iWb.removeWebviewEventListener('GO_BACK', listenerMobileBack)
      }
    }
    return () => null
  }, [
    Window,
    iWb,
    isApp,
    isNotFirstScreen,
    location?.pathname,
    navigate,
    origemGoBackValue,
    handleRootBackForOldBridge,
    listenerMobileBack,
    redirectToGoBackOrigin,
    shouldHandleAsRootBack,
    shouldRedirectToEmprestimoFromContratos,
  ])

  return <></>
}
